import * as React from 'react';

// components
import Text from 'components/Text';
import Divider from 'components/Divider';
import Checkbox from 'components/Checkbox';
import MediaObject from 'components/MediaObject';
import { GetLanguageSet_translations_getLanguageSet_data_languageCounts as LanguageCounts } from 'translations/queries/__generated__/GetLanguageSet';
import './styles/TranslationsHoverCard.scss';
import { mergeLanguageResults } from 'translations/utils';
import TooltipTrigger from 'components/TooltipTrigger';
import Icon from 'components/Icon';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import { TranslationsHoverCardRow } from './TranslationsHoverCardRow';
import { Menu } from 'components/Menu';
import MenuItem from 'components/MenuItem';
import { translatedMessage, TranslatedMessagePayload } from 'translations/actions';
import { SuccessResponse } from 'translations/types';
import errorLogger from 'utils/errorLogger';

type Props = {
  languageCounts: LanguageCounts[];
  defaultLanguageCode: string;
  toggleTranslate: (shouldTranslate: boolean) => void;
  shouldTranslate: boolean;
  onMenuItemSelect?: (languageCode: String) => void;
  setTranslationPreview?: (
    translateMessageResponse: SuccessResponse<TranslatedMessagePayload>
  ) => void;
  backToOriginalCallback?: () => void;
  textToTranslate: string;
  translationsPreviewActive: boolean;
  toggleHoverCardVisibility: () => void;
};

type State = {
  mergedLanguageResults: LanguageCounts[];
};

// Generic presentation component that should (hopefully) be reusable across
// both ChatBroadcast and Chat composers.
export class TranslationsHoverCard extends React.Component<Props, State> {
  state: State = {
    mergedLanguageResults: [],
  };

  componentDidMount() {
    this.updateLanguageCount();
  }

  componentWillUpdate(prevProps: Readonly<Props>) {
    if (prevProps.languageCounts !== this.props.languageCounts) {
      this.updateLanguageCount();
    }
  }

  updateLanguageCount = () => {
    const { languageCounts } = this.props;
    let mergedLanguageResults = mergeLanguageResults(languageCounts).sort(
      (a, b) => b.count - a.count
    );
    const noPreference = mergedLanguageResults.filter(lc => lc.languageCode === 'none');
    mergedLanguageResults = mergedLanguageResults.filter(lc => lc.languageCode !== 'none');

    this.setState({
      mergedLanguageResults: [...mergedLanguageResults, ...noPreference],
    });
  };

  displayTranslatedMessagePreview = (languageCode: string) => {
    const { textToTranslate, setTranslationPreview, defaultLanguageCode } = this.props;
    // don't even bother apollo if a preference isn't set
    if (setTranslationPreview) {
      if (languageCode === 'none') {
        const noPreference = this.props.languageCounts.filter(lc => lc.languageCode === 'none')[0];
        const response: SuccessResponse<TranslatedMessagePayload> = {
          success: true,
          languageCode: 'none',
          translatedText: `${noPreference.count} recipients haven't set a preferred
          language and will receive the original message. To change someone's preferred language, edit their
          profile from the people list.`,
        };
        setTranslationPreview(response);
      } else {
        const translatedMessageResponse = translatedMessage({
          languageCode: defaultLanguageCode,
          targetLanguageCode: languageCode,
          text: textToTranslate,
        });
        translatedMessageResponse
          .then(response => {
            if (response.success) {
              setTranslationPreview(response);
            }
          })
          .catch(error => {
            errorLogger(error);
          });
      }
    }
  };

  renderLanguageOptions = () => {
    const { shouldTranslate, defaultLanguageCode } = this.props;
    const { mergedLanguageResults } = this.state;
    return mergedLanguageResults.map((languageCount: LanguageCounts) => {
      const { languageCode, count } = languageCount;
      return (
        count !== 0 && (
          <TranslationsHoverCardRow
            {...this.props}
            key={languageCode}
            disabled={languageCode === defaultLanguageCode || !shouldTranslate}
            onSelect={() => {
              this.displayTranslatedMessagePreview(languageCode);
              this.props.toggleHoverCardVisibility();
            }}
            languageCount={languageCount}
          />
        )
      );
    });
  };

  renderWithTranslationsPreview = () => {
    const { backToOriginalCallback } = this.props;
    return (
      <Menu rowHover className="TranslationsHoverCard-body" hideOnSelect={false} {...this.props}>
        <Text align="left">{this.renderLanguageOptions()}</Text>
        <Divider size="small" />
        <MenuItem
          menuKey="back_to_original"
          selected
          onSelect={() => {
            if (backToOriginalCallback) {
              backToOriginalCallback();
            }
          }}>
          <Text size="small" color="mascot" lineHeight="flush">
            Back to original
          </Text>
        </MenuItem>
      </Menu>
    );
  };

  renderWithOutTranslationsPreview = () => {
    const { toggleTranslate, shouldTranslate } = this.props;
    return (
      <Menu rowHover className="TranslationsHoverCard-body" hideOnSelect {...this.props}>
        <MediaObject figure="right" className="TranslationsHoverCard-first-menu-item">
          <Checkbox
            flush
            onClick={() => {
              toggleTranslate(!shouldTranslate);
            }}
            label={<Text weight="semibold">Translate this message</Text>}
            checked={shouldTranslate}
          />
          <TooltipTrigger tooltip="Learn More" placement="top">
            <Link color="graphite" href={LinkConstants.TRANSLATIONS_TOOLTIP_HELP} target="_blank">
              <Icon name="question-circle" />
            </Link>
          </TooltipTrigger>
        </MediaObject>
        <Divider size="small" />
        <Text align="left">{this.renderLanguageOptions()}</Text>
      </Menu>
    );
  };

  render() {
    const { translationsPreviewActive } = this.props;
    return translationsPreviewActive
      ? this.renderWithTranslationsPreview()
      : this.renderWithOutTranslationsPreview();
  }
}
